import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ProfilePicture from '@/components/ProfilePicture';
import { ClipboardCheck, Lock, Users2, CheckCircle } from 'lucide-react';
import { HomeworkAttachment } from '@/components/HomeworkAttachment';

export const HomeworkReviewQueue = () => {
  const [tab, setTab] = useState('pending');
  const [type, setType] = useState('all');
  const [subs, setSubs] = useState([]);
  const [notice, setNotice] = useState(null);
  const [counts, setCounts] = useState({});
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sections/homework/queue?status=${tab}&type=${type}`);
      setSubs(r.data.submissions || []);
      setNotice(r.data.notice || null);
      setCounts(r.data.counts || {});
      setLabels(r.data.labels || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load queue');
    } finally { setLoading(false); }
  }, [tab, type]);

  useEffect(() => { load(); }, [load]);

  const review = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/sections/homework/${id}/review`, { feedback: feedback[id] || '' });
      toast.success(`Reviewed — +${r.data.points_awarded} points`);
      setFeedback((f) => ({ ...f, [id]: '' }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to review');
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="homework-review-queue">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck className="w-6 h-6 text-purple-600" />
        <h2 className="text-2xl font-bold">Homework Review</h2>
      </div>
      <div className="flex gap-2 mb-4">
        {['pending', 'reviewed'].map((t) => (
          <Button key={t} size="sm" variant={tab === t ? 'default' : 'outline'} onClick={() => setTab(t)} data-testid={`hw-tab-${t}`} className={tab === t ? 'bg-gradient-to-r from-cyan-500 to-purple-500 capitalize' : 'capitalize'}>{t}</Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="hw-type-filter">
        {[['all', 'Everything'], ...Object.entries(labels)].map(([key, label]) => {
          const n = key === 'all'
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[key] || 0);
          if (key !== 'all' && n === 0) return null;
          return (
            <button
              key={key}
              onClick={() => setType(key)}
              data-testid={`hw-type-${key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                type === key
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}
            >
              {label} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : notice ? (
        <div
          className="py-10 px-6 text-center rounded-lg border border-amber-400/30 bg-amber-400/10"
          data-testid="hw-no-sections-notice"
        >
          <ClipboardCheck className="w-8 h-8 mx-auto mb-3 text-amber-400" />
          <p className="text-sm text-amber-200/90 max-w-md mx-auto">{notice}</p>
        </div>
      ) : subs.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">No {tab} homework right now.</p>
      ) : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.id} className="border rounded-lg p-4 bg-white" data-testid={`review-item-${s.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <ProfilePicture src={s.user_avatar} alt={s.username} size="sm" />
                  <div>
                    <p className="font-medium text-sm text-gray-900">{s.username}</p>
                    <p className="text-xs text-gray-600">{s.module_title} · {s.section_name}</p>
                  </div>
                </div>
                <span className="text-xs flex items-center gap-2 text-gray-400">
                  <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium" data-testid={`review-type-${s.id}`}>{s.content_label}</span>
                  {s.visibility === 'public' ? <><Users2 className="w-3 h-3" /> Shared</> : <><Lock className="w-3 h-3" /> Private</>}
                </span>
              </div>
              {s.worksheet_title && (
                <p className="text-sm font-semibold text-gray-800 mt-2">{s.worksheet_title}</p>
              )}
              <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap bg-gray-50 rounded p-3">{s.content}</p>
              {s.file_url && (
                <HomeworkAttachment url={s.file_url} name={s.file_name} testIdSuffix={`-${s.id}`} />
              )}

              {tab === 'pending' ? (
                <div className="mt-3 space-y-2">
                  <Textarea
                    value={feedback[s.id] || ''}
                    onChange={(e) => setFeedback((f) => ({ ...f, [s.id]: e.target.value }))}
                    placeholder="Feedback (optional, +2 points)"
                    className="min-h-[60px]"
                    data-testid={`feedback-${s.id}`}
                  />
                  <Button size="sm" disabled={busy === s.id} onClick={() => review(s.id)} data-testid={`mark-reviewed-${s.id}`} className="bg-gradient-to-r from-green-500 to-emerald-600">
                    <CheckCircle className="w-4 h-4 mr-1" /> Mark Reviewed
                  </Button>
                </div>
              ) : (
                s.feedback && (
                  <div className="mt-2 bg-purple-50 border-l-2 border-purple-400 pl-2 py-1 text-purple-800 text-xs">
                    <span className="font-medium">{s.reviewed_by_name}:</span> {s.feedback}
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeworkReviewQueue;
